import React, { useState } from "react";
import { CheckCircle2, XCircle, Clock, CalendarClock, Receipt } from "lucide-react";
import { api, formatError } from "../../lib/api.js";
import { fmtDateTime } from "../../lib/format.js";
import { BpsAttachmentSlot } from "./BpsAttachmentSlot.jsx";
import { DamageEmailApproval } from "./DamageEmailApproval.jsx";
import { toLocalInput, fromLocalInput, openAttachment } from "../../lib/bps.js";

const APPROVERS = [
  { key: "insurance", label: "Seguradora", category: "repair_order", required: true },
  { key: "customer", label: "Cliente privado", category: "payment_proof", required: false },
  { key: "fleet", label: "Empresa / Frota", category: "payment_proof", required: false },
];

const ApprovalStatus = ({ a }) => {
  if (!a) return <span className="text-[#6C757D]">Sem registo</span>;
  return a.approved
    ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Aprovado · {a.approved_by_name || "—"} · {fmtDateTime(a.approved_at)}</span>
    : <span className="text-[#DC3545] inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Não aprovado · {fmtDateTime(a.decided_at)}</span>;
};

export const ApprovalsPanel = ({ bps, attachments, onAttachmentsChange, onReload, setErr }) => {
  const latest = (cat) => [...attachments].filter((x) => x.category === cat).sort((a, b) => (a.uploaded_at < b.uploaded_at ? 1 : -1))[0]?.id || null;
  const approve = async (type, approved) => {
    setErr("");
    const attachment_id = latest(type === "insurance" ? "repair_order" : "payment_proof");
    try { await api.post(`/body-shop-cases/${bps.id}/approval`, { approver_type: type, approved, attachment_id }); onReload(); }
    catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      {APPROVERS.map((t) => {
        const a = (bps.approvals || {})[t.key];
        const missingRO = t.required && !attachments.some((x) => x.category === t.category);
        return (
          <div key={t.key} className="bg-white border border-gray-200 rounded-sm p-3 space-y-2" data-testid={`bps-approval-${t.key}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-sm">{t.label}</div>
                <div className="text-xs mt-0.5"><ApprovalStatus a={a} /></div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => approve(t.key, true)} disabled={missingRO} title={missingRO ? "Anexe a ordem de reparação primeiro" : ""} className="text-xs border border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white rounded-sm px-2 py-1 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-emerald-700" data-testid={`bps-approve-${t.key}-yes`}>Aprovar</button>
                <button onClick={() => approve(t.key, false)} className="text-xs border border-[#DC3545] text-[#DC3545] hover:bg-[#DC3545] hover:text-white rounded-sm px-2 py-1" data-testid={`bps-approve-${t.key}-no`}>Rejeitar</button>
              </div>
            </div>
            {t.key === "insurance" && (
              <BpsAttachmentSlot bpsId={bps.id} category="repair_order" required attachments={attachments} onUploaded={onAttachmentsChange} />
            )}
          </div>
        );
      })}
      <BpsAttachmentSlot bpsId={bps.id} category="payment_proof" label="Comprovativo de pagamento (cliente privado / empresa)" multiple attachments={attachments} onUploaded={onAttachmentsChange} />
    </div>
  );
};

const DAMAGE_STATUS = {
  pending_approval: { label: "A aguardar aprovação", cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: Clock },
  approved: { label: "Aprovado", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
  rejected: { label: "Rejeitado", cls: "bg-red-50 text-red-800 border-red-300", Icon: XCircle },
};

const DamageRow = ({ d, bps, attachments, bpsId, onReload, setErr }) => {
  const [approver, setApprover] = useState("customer");
  const st = DAMAGE_STATUS[d.status] || DAMAGE_STATUS.pending_approval;
  const quote = attachments.find((a) => a.id === d.quote_attachment_id);
  const decide = async (approved) => {
    setErr("");
    try { await api.post(`/body-shop-cases/${bpsId}/additional-damage/${d.id}/decision`, { approved, approver_type: approver }); onReload(); }
    catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };
  return (
    <li className="border border-gray-200 rounded-sm p-2.5 text-sm" data-testid={`bps-damage-${d.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="font-medium">{d.description}</div>
          <div className="text-xs text-[#6C757D]">Registado {fmtDateTime(d.reported_at)}{quote && <> · <button onClick={() => openAttachment(quote)} className="text-[#1F3864] hover:underline">{quote.original_filename}</button></>}</div>
          {d.decided_at && <div className="text-xs text-[#6C757D]">{d.decision_approver_type === "insurance" ? "Seguradora" : "Cliente"} · {d.decided_by_name} · {fmtDateTime(d.decided_at)}</div>}
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${st.cls}`} data-testid={`bps-damage-status-${d.id}`}><st.Icon className="w-3 h-3" />{st.label}</span>
      </div>
      {d.status === "pending_approval" && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <select value={approver} onChange={(e) => setApprover(e.target.value)} className="text-xs border border-gray-300 rounded-sm px-1.5 py-1" data-testid={`bps-damage-approver-${d.id}`}>
            <option value="customer">Cliente</option><option value="insurance">Seguradora</option>
          </select>
          <button onClick={() => decide(true)} className="text-xs border border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white rounded-sm px-2 py-1" data-testid={`bps-damage-approve-${d.id}`}>Aprovado</button>
          <button onClick={() => decide(false)} className="text-xs border border-[#DC3545] text-[#DC3545] hover:bg-[#DC3545] hover:text-white rounded-sm px-2 py-1" data-testid={`bps-damage-reject-${d.id}`}>Rejeitado</button>
        </div>
      )}
      {d.status === "pending_approval" && <DamageEmailApproval bps={bps} damage={d} onReload={onReload} />}
      {d.decided_via === "email" && d.decision_comment && <div className="mt-1 text-xs text-[#6C757D] italic">Comentário do cliente: {d.decision_comment}</div>}
    </li>
  );
};

export const DamagesPanel = ({ bps, attachments, onAttachmentsChange, onReload, setErr }) => {
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [quoteId, setQuoteId] = useState(null);
  const pendingQuote = attachments.find((a) => a.id === quoteId);
  const add = async () => {
    setErr("");
    try {
      await api.post(`/body-shop-cases/${bps.id}/additional-damage`, { description: desc, estimated_amount: amount ? Number(amount) : null, quote_attachment_id: quoteId });
      setDesc(""); setAmount(""); setQuoteId(null); onReload();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };
  const list = bps.additional_damages || [];
  return (
    <div className="space-y-3">
      <p className="text-xs text-[#6C757D]">A oficina continua a trabalhar. O caso só pode ser facturado quando todos os danos adicionais estiverem decididos.</p>
      <div className="space-y-2 border border-dashed border-gray-300 rounded-sm p-3">
        <input placeholder="Descrição do dano adicional…" value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="bps-damage-desc" />
        <input type="number" min="0" placeholder="Valor estimado (MZN, opcional)" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm font-mono" data-testid="bps-damage-amount" />
        <BpsAttachmentSlot bpsId={bps.id} category="additional_quote" label="Orçamento adicional" attachments={pendingQuote ? [pendingQuote] : []} onUploaded={(up) => { setQuoteId(up[0]?.id); onAttachmentsChange(); }} />
        <button onClick={add} disabled={!desc.trim()} className="bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-1.5 text-sm disabled:opacity-50" data-testid="bps-damage-add">Registar dano adicional</button>
      </div>
      {list.length === 0 ? <div className="text-sm text-[#6C757D] italic text-center py-4">Sem danos adicionais registados.</div> : (
        <ul className="space-y-2">{list.map((d) => <DamageRow key={d.id} d={d} bps={bps} attachments={attachments} bpsId={bps.id} onReload={onReload} setErr={setErr} />)}</ul>
      )}
    </div>
  );
};

export const ScheduleCard = ({ bps, onReload, setErr }) => {
  const [editing, setEditing] = useState(false);
  const [entry, setEntry] = useState(toLocalInput(bps.scheduled_entry_at));
  const [exit, setExit] = useState(toLocalInput(bps.expected_exit_at));
  if (!bps.scheduled_entry_at) return null;
  const save = async () => {
    setErr("");
    try { await api.put(`/body-shop-cases/${bps.id}/schedule`, { scheduled_entry_at: fromLocalInput(entry), expected_exit_at: fromLocalInput(exit) }); setEditing(false); onReload(); }
    catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="bps-schedule-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-heading font-semibold text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#1F3864]" /> Agendamento</div>
        {!editing && <button onClick={() => setEditing(true)} className="text-xs text-[#1F3864] hover:underline" data-testid="bps-schedule-edit">Actualizar previsão</button>}
      </div>
      {editing ? (
        <div className="grid grid-cols-2 gap-2 items-end">
          <input type="datetime-local" value={entry} onChange={(e) => setEntry(e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1 text-sm" data-testid="bps-schedule-edit-entry" />
          <input type="datetime-local" value={exit} onChange={(e) => setExit(e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1 text-sm" data-testid="bps-schedule-edit-exit" />
          <div className="col-span-2 flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="text-xs px-3 py-1 border border-gray-300 rounded-sm">Cancelar</button>
            <button onClick={save} className="text-xs px-3 py-1 bg-[#1F3864] text-white rounded-sm" data-testid="bps-schedule-save">Guardar</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Entrada</div><div data-testid="bps-schedule-entry-value">{fmtDateTime(bps.scheduled_entry_at)}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Previsão de saída</div><div data-testid="bps-schedule-exit-value">{fmtDateTime(bps.expected_exit_at)}</div></div>
        </div>
      )}
      {(bps.schedule_history || []).length > 1 && (
        <details className="mt-2 text-xs text-[#6C757D]">
          <summary className="cursor-pointer">Histórico ({bps.schedule_history.length})</summary>
          <ul className="mt-1 space-y-0.5">{bps.schedule_history.map((h, i) => <li key={i}>{fmtDateTime(h.changed_at)} · {h.changed_by_name}: {fmtDateTime(h.scheduled_entry_at)} → {fmtDateTime(h.expected_exit_at)}</li>)}</ul>
        </details>
      )}
    </div>
  );
};

export const InvoiceCard = ({ bps, enums }) => {
  if (!bps.invoice) return null;
  const labels = Object.fromEntries((enums.invoice_types || []).map((t) => [t.key, t.label]));
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="bps-invoice-card">
      <div className="font-heading font-semibold text-sm flex items-center gap-2 mb-2"><Receipt className="w-4 h-4 text-[#1F3864]" /> Factura</div>
      <div className="text-sm"><span className="font-mono font-semibold" data-testid="bps-invoice-number-value">{bps.invoice.number}</span> · {bps.invoice.types.map((t) => labels[t] || t).join(" + ")}</div>
      <div className="text-xs text-[#6C757D] mt-0.5">{bps.invoice.billed_by_name} · {fmtDateTime(bps.invoice.billed_at)}</div>
    </div>
  );
};
